import React, {useEffect, useRef} from 'react';
import {useMap} from 'react-leaflet';
import L from 'leaflet';
import '../../styles/DrawingControl.css'; // Import des styles spécifiques

interface DrawingControlProps {
    onTrackCreated?: (latlngs: L.LatLng[]) => void;
    mode?: 'walking' | 'cycling' | 'driving';
    provider?: 'osrm' | 'ign';
    initialWaypoints?: L.LatLng[];
}

interface RouteResponse {
    routes: Array<{
        geometry: {
            type: string;
            coordinates: [number, number][];
        };
        distance: number;
        duration: number;
        distance_text?: string;
        duration_text?: string;
    }>;
}

const DrawingControl: React.FC<DrawingControlProps> = ({onTrackCreated, mode = 'walking', provider = 'osrm', initialWaypoints}) => {
    const map = useMap();
    const drawItemsRef = useRef<L.FeatureGroup | null>(null);
    const routeLayerRef = useRef<L.Polyline | null>(null);
    const markersLayerRef = useRef<L.FeatureGroup | null>(null);
    const waypointsRef = useRef<L.LatLng[]>([]);
    const markerListRef = useRef<L.Marker[]>([]);
    // Refs pour éviter que le changement de callback/initialisation ne relance l'effet
    const onTrackCreatedRef = useRef(onTrackCreated);
    useEffect(() => { onTrackCreatedRef.current = onTrackCreated; });
    const initialWaypointsRef = useRef(initialWaypoints);

    const ROUTE_API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/route`;


    useEffect(() => {
        // Initialiser les groupes de calques
        drawItemsRef.current = L.featureGroup().addTo(map);
        markersLayerRef.current = L.featureGroup().addTo(map);

        // Démarrer automatiquement le dessin à la création
        startDrawing();

        // Pré-remplir les waypoints initiaux si fournis (mode édition)
        const initWps = initialWaypointsRef.current;
        if (initWps && initWps.length > 0) {
            initWps.forEach(latlng => createMarker(latlng));
            if (waypointsRef.current.length >= 2) {
                calculateRoute();
            }
        }

        function startDrawing() {
            // Réinitialiser
            clearLayers();
            waypointsRef.current = [];

            // Activer le mode dessin
            map.on('click', addWaypoint);
            map.getContainer().style.cursor = 'crosshair';
        }

        function clearLayers() {
            if (drawItemsRef.current) drawItemsRef.current.clearLayers();
            if (markersLayerRef.current) markersLayerRef.current.clearLayers();
            routeLayerRef.current = null;
            waypointsRef.current = [];
            markerListRef.current = [];
        }

        function addWaypoint(e: L.LeafletMouseEvent) {
            createMarker(e.latlng);
            // Calculer un itinéraire si nous avons au moins 2 points
            if (waypointsRef.current.length >= 2) {
                calculateRoute();
            }
        }

        function createMarker(latlng: L.LatLng) {
            waypointsRef.current.push(latlng);

            // Ajouter un marqueur pour le point avec une icône personnalisée
            if (markersLayerRef.current) {
                // Définir une icône personnalisée pour le marqueur
                const waypointIcon = L.divIcon({
                    className: 'custom-waypoint-marker',
                    html: `<div style="
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background-color: #ffffff;
                border: 3px solid #3388ff;
                box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
                box-sizing: content-box;
            "></div>`,
                    iconSize: [22, 22],
                    iconAnchor: [11, 11],
                });

                const marker = L.marker(latlng, {
                    draggable: true,
                    icon: waypointIcon
                }).addTo(markersLayerRef.current);
                markerListRef.current.push(marker);

                // Ajouter un numéro pour indiquer l'ordre des points
                const waypointNumber = waypointsRef.current.length;
                marker.bindTooltip(waypointNumber.toString(), {
                    permanent: true,
                    direction: 'center',
                    className: 'waypoint-number-tooltip'
                });

                // Permettre de supprimer un point par clic droit
                marker.on('contextmenu', function () {
                    const index = markerListRef.current.indexOf(marker);

                    if (index >= 0) {
                        waypointsRef.current.splice(index, 1);
                        markerListRef.current.splice(index, 1);
                        markersLayerRef.current?.removeLayer(marker);

                        // Recalculer le parcours
                        if (waypointsRef.current.length >= 2) {
                            calculateRoute();

                            // Mettre à jour les numéros des waypoints restants
                            markerListRef.current.forEach((m, i) => {
                                m.setTooltipContent((i + 1).toString());
                            });
                        } else if (routeLayerRef.current) {
                            drawItemsRef.current?.removeLayer(routeLayerRef.current);
                            routeLayerRef.current = null;
                        }
                    }
                });

                // Mettre à jour le tracé quand on déplace un marqueur
                marker.on('dragend', function () {
                    const index = markerListRef.current.indexOf(marker);

                    if (index >= 0) {
                        waypointsRef.current[index] = marker.getLatLng();

                        if (waypointsRef.current.length >= 2) {
                            calculateRoute();
                        }
                    }
                });
            }
        }

        async function calculateRoute() {
            try {
                if (waypointsRef.current.length < 2) return;

                const points = waypointsRef.current.map(p => `${p.lat},${p.lng}`).join(';');
                const params = new URLSearchParams({ points, mode, provider, geometries: 'geojson' });
                const response = await fetch(`${ROUTE_API_URL}?${params.toString()}`);

                if (!response.ok) {
                    throw new Error(`Erreur HTTP : ${response.status} - ${response.statusText}`);
                }

                const data: RouteResponse = await response.json();
                if (!data.routes || data.routes.length === 0) {
                    console.error('Aucun itinéraire retourné:', data);
                    return;
                }

                const route = data.routes[0];
                const latLngs = route.geometry.coordinates.map((coord: [number, number]) =>
                    L.latLng(coord[1], coord[0])
                );
                const distanceKm = (route.distance / 1000).toFixed(2);
                const durationMin = Math.floor(route.duration / 60);

                // Afficher l'itinéraire
                if (routeLayerRef.current) {
                    drawItemsRef.current?.removeLayer(routeLayerRef.current);
                }

                routeLayerRef.current = L.polyline(latLngs, {
                    color: '#f35757',
                    weight: 5,
                    opacity: 0.7
                });
                if (drawItemsRef.current) {
                    routeLayerRef.current.addTo(drawItemsRef.current);
                }

                routeLayerRef.current.bindTooltip(
                    `Distance: ${distanceKm} km<br>Durée: ~${durationMin} min`,
                    {permanent: false, direction: 'auto'}
                );

            } catch (error) {
                console.error('Erreur lors du calcul de l\'itinéraire:', error);
            }
        }

        return () => {
            map.off('click', addWaypoint);
            // Passer les coordonnées de la trace au parent avant de nettoyer
            if (routeLayerRef.current && onTrackCreatedRef.current) {
                const latlngs = routeLayerRef.current.getLatLngs() as L.LatLng[];
                if (latlngs.length > 0) {
                    onTrackCreatedRef.current(latlngs);
                }
            }
            clearLayers();
            map.getContainer().style.cursor = '';
        };
    }, [map, mode, provider, ROUTE_API_URL]);

    return null;
};

export default DrawingControl;